/**
 * OPEN-136 / DEC-377 — a tank's bbl per inch when nobody entered one.
 *
 * DEC-351 estimates a SINGLE_GAUGE level as top gauge × the tank's bbl_per_inch. Corbin: "most
 * haulers will not enter this information." So when the tank has none, the estimate uses the
 * standard tank catalog, in this order:
 *   1. the catalog row the tank is linked to (standard_tank_catalog_id);
 *   2. else, the active rows with exactly its capacity, when they share ONE value.
 * The level is still an estimate (flagged, labelled "est.") and the hauler's
 * tank_estimates_enabled switch still drops it.
 *
 * There is no tank material. Corbin (2026-10-29): "all of these tanks hold water. What the tank
 * is made of is not really needed." DEC-379 (0265) dropped the catalog's type column.
 *
 * Ambiguous sizes stay without an estimate: a 500 BBL tank is 15 ft 6 in (2.75) or
 * 15 ft 6.5 in (2.79) across, and is never guessed from capacity (Corbin: "don't have a bare
 * 500"). A tank with no capacity has nothing to match.
 *
 * The operator portal knows only each tank's capacity, so it passes no link and gets step 2
 * (OPEN-188 gives it step 1). Its estimates are display only; the sweeper uses both steps.
 *
 * NEVER for a RECORDED level: a hand reading in inches (0245) keeps waiting for a real size.
 */

/** A standard_tank_catalog row, as the API reads it. Rows without isActive are active. */
export interface StandardTankSize {
  id?: string;
  name: string;
  capacityBbl: string | number;
  bblPerInch: string | number;
  isActive?: boolean;
}

/** What the rule needs to know about a tank. Omit the link for capacity only. */
export interface TankSizeFacts {
  bblPerInch?: string | number | null;
  capacityBbl: string | number | null;
  standardTankCatalogId?: string | null;
}

/** One distinct value the catalog offers for a tank. */
export interface BblPerInchChoice {
  /** The first catalog name with this value. */
  name: string;
  capacityBbl: string;
  bblPerInch: string;
}

export interface BblPerInchSuggestion {
  /** The one value the rule gives; null when there are none or several. */
  bblPerInch: string | null;
  /** Each distinct value that fits, so an ambiguous size can say what the options are. */
  choices: BblPerInchChoice[];
}

function num(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** The rows, one choice per distinct value (review LOW-2), in catalog order. */
function toChoices(rows: readonly StandardTankSize[]): BblPerInchChoice[] {
  const byValue = new Map<number, StandardTankSize>();
  for (const r of rows) {
    const v = num(r.bblPerInch);
    if (v !== null && v > 0 && !byValue.has(v)) byValue.set(v, r);
  }
  return [...byValue.values()].map((r) => ({
    name: r.name,
    capacityBbl: String(r.capacityBbl),
    bblPerInch: String(r.bblPerInch),
  }));
}

/** The catalog's suggestion for a tank (its own bbl_per_inch is not looked at). */
export function suggestBblPerInch(
  tank: TankSizeFacts,
  catalog: readonly StandardTankSize[],
): BblPerInchSuggestion {
  if (tank.standardTankCatalogId != null) {
    const linked = catalog.find((c) => c.id === tank.standardTankCatalogId);
    if (linked !== undefined) {
      const choices = toChoices([linked]);
      return { bblPerInch: choices[0]?.bblPerInch ?? null, choices };
    }
  }
  const cap = num(tank.capacityBbl);
  if (cap === null || cap <= 0) return { bblPerInch: null, choices: [] };
  const choices = toChoices(
    catalog.filter((c) => c.isActive !== false && num(c.capacityBbl) === cap),
  );
  return { bblPerInch: choices.length === 1 ? choices[0]!.bblPerInch : null, choices };
}

export interface EffectiveBblPerInch {
  bblPerInch: string | null;
  /** TANK: entered on the tank. CATALOG: the rule above. null: none known. */
  source: 'TANK' | 'CATALOG' | null;
}

/** What the gauge estimate uses: the tank's own positive value, else the catalog's single one. */
export function effectiveBblPerInch(
  tank: TankSizeFacts,
  catalog: readonly StandardTankSize[],
): EffectiveBblPerInch {
  const mine = num(tank.bblPerInch);
  if (mine !== null && mine > 0) return { bblPerInch: String(tank.bblPerInch), source: 'TANK' };
  const s = suggestBblPerInch(tank, catalog);
  return s.bblPerInch === null
    ? { bblPerInch: null, source: null }
    : { bblPerInch: s.bblPerInch, source: 'CATALOG' };
}

// ── DEC-379: the BBL/inch sanity warning ────────────────────────────────────
//
// BBL/inch is typed from the tank's strapping chart, and every gauge-to-barrel conversion uses
// that typed value; it is never overwritten with a calculated one. When the catalog row has its
// dimensions, the geometry gives an expected value, and a typed value more than 5% away from it
// gets a warning that never blocks saving. Real strapped values run up to about 2% off the
// formula, so 5% is the threshold. The admin catalog form and table both use this.

export type TankShape = 'ROUND' | 'RECTANGULAR';

/** A catalog row's dimensions, in feet. Missing or non-positive values mean "not known". */
export interface TankDimensions {
  shape: TankShape;
  diameterFt?: string | number | null;
  lengthFt?: string | number | null;
  widthFt?: string | number | null;
}

/** bbl/in per square foot of diameter for a round tank: π/4 × 1/12 ft ÷ 5.6146 ft³/bbl. */
export const ROUND_BBL_PER_INCH_PER_SQ_FT = 0.011657;
/** Square feet of floor per bbl/in for a rectangular tank: 12 in/ft × 5.6146 ft³/bbl. */
export const RECT_SQ_FT_PER_BBL_PER_INCH = 67.375;
/** A typed value more than this far (in percent) from the estimate gets the warning. */
export const BBL_PER_INCH_WARN_PCT = 5;

function positive(v: string | number | null | undefined): number | null {
  const n = num(v);
  return n !== null && n > 0 ? n : null;
}

/** The dimension-based bbl per inch, or null when the shape's dimensions are missing. */
export function expectedBblPerInch(d: TankDimensions): number | null {
  if (d.shape === 'ROUND') {
    const dia = positive(d.diameterFt);
    return dia === null ? null : ROUND_BBL_PER_INCH_PER_SQ_FT * dia * dia;
  }
  const len = positive(d.lengthFt);
  const wid = positive(d.widthFt);
  return len === null || wid === null ? null : (len * wid) / RECT_SQ_FT_PER_BBL_PER_INCH;
}

export interface BblPerInchWarning {
  /** The dimension-based estimate. */
  expectedBblPerInch: number;
  /** How far the typed value is from it, in percent (positive: typed is higher). */
  deviationPct: number;
  /** "BBL/inch differs from the dimension-based estimate by 143.7%". */
  message: string;
}

/**
 * The warning for a typed bbl per inch, or null when it is within 5%, when the dimensions are
 * missing, or when nothing (or nothing positive) is typed. The deviation is rounded to 0.01% so
 * exactly 5% does not warn on floating-point noise.
 */
export function bblPerInchWarning(
  typedBblPerInch: string | number | null | undefined,
  d: TankDimensions,
): BblPerInchWarning | null {
  const typed = positive(typedBblPerInch);
  const expected = expectedBblPerInch(d);
  if (typed === null || expected === null) return null;
  const deviationPct = Math.round(((typed - expected) / expected) * 10000) / 100;
  if (Math.abs(deviationPct) <= BBL_PER_INCH_WARN_PCT) return null;
  const shown = Math.round(Math.abs(deviationPct) * 10) / 10;
  return {
    expectedBblPerInch: expected,
    deviationPct,
    message: `BBL/inch differs from the dimension-based estimate by ${String(shown)}%`,
  };
}

/** A catalog row as a size picker shows it. */
export interface StandardTankLabelFacts extends Partial<TankDimensions> {
  name: string;
  heightFt?: string | number | null;
}

function feet(v: string | number | null | undefined): string | null {
  const n = positive(v);
  return n === null ? null : String(Number(n.toFixed(4)));
}

/**
 * A picker option's label: the row's name, then its size when known, so the two 500 BBL rows
 * read apart ("500 BBL 15FT 6IN · 15.5 ft across, 16 ft tall"). Corbin: "don't have a bare 500".
 */
export function standardTankLabel(t: StandardTankLabelFacts): string {
  const h = feet(t.heightFt);
  const across =
    t.shape === 'RECTANGULAR'
      ? feet(t.lengthFt) !== null && feet(t.widthFt) !== null
        ? `${feet(t.lengthFt) ?? ''} × ${feet(t.widthFt) ?? ''} ft`
        : null
      : feet(t.diameterFt) !== null
        ? `${feet(t.diameterFt) ?? ''} ft across`
        : null;
  const size = [across, h === null ? null : `${h} ft tall`].filter((s) => s !== null).join(', ');
  return size === '' ? t.name : `${t.name} · ${size}`;
}

/** An active catalog row as a size picker gets it (the portal's /tank-setup/standard-tanks). */
export interface StandardTankOption {
  id: string;
  name: string;
  capacityBbl: string;
  bblPerInch: string;
  shape: TankShape;
  diameterFt: string | null;
  lengthFt: string | null;
  widthFt: string | null;
  heightFt: string | null;
}
